import React, { Suspense, useEffect, useMemo, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import Header from '@components/Header'
import Footer from '@components/Footer'
import Alert from '@components/Alert'
import { GlobalLoading } from '@components/LoadingSpinner'
import { ToastContainer } from '@/components/ui/ToastContainer'
import { setupTranslationDetection, updateFontSizes } from '@utils'
import { getClassFromObject } from '@/utils/dom'
import { useSessionStore, useUIStore } from '@/stores'
import { useCartStore } from '@/stores/useCartStore'
import { preloadImage, preloadImages } from '@/utils/imageCache'
import Lifeline from '@/components/Lifeline'
import FloatingButtons from '@/components/FloatingButtons'
import { useIframeCommunication } from '@/hooks/useIframeCommunication'
import { isEmbedded } from '@utils/iframe'
import { useEmbedConfig } from '@/hooks/useEmbedConfig'
import { InputType, CUSTOMER_PORTAL_STEP_TYPES } from '@/constants'
import { cn } from '@utils'

function resolveItemImageUrls(item: any): string[] {
  const cp = item?.campaign_product
  return [
    cp?.image?.file?.medium,
    cp?.image?.file?.original,
    cp?.product?.default_image?.file?.medium,
    cp?.product?.default_image?.file?.original,
    cp?.product?.images?.[0]?.file?.medium,
    cp?.product?.images?.[0]?.file?.original,
  ].filter(Boolean) as string[]
}

function SuspenseFallback() {
  return <div className="flex-1 min-h-[200px]" />
}

// Error boundary component
class RouteErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean; error?: Error }> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Route error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="text-center p-8">
          <h2 className="text-xl font-semibold mb-3" style={{ color: 'var(--text-color)' }}>
            Something went wrong
          </h2>
          <p className="mb-4" style={{ color: 'var(--medium-text-color)' }}>
            We're sorry, but something unexpected happened.
          </p>
          <button onClick={() => window.location.reload()} className="btn-primary">
            Reload Page
          </button>
        </div>
      )
    }

    return this.props.children
  }
}

const Layout = () => {
  const location = useLocation()
  const [isLanguageReady, setIsLanguageReady] = useState(false)
  const session = useSessionStore(s => s.session) as any
  const isBannerHidden = useUIStore(s => s.isBannerHidden)
  const bannerHtml = useMemo(() => updateFontSizes(session?.banner || ''), [session?.banner])
  const showBanner = !!bannerHtml && !isBannerHidden
  useIframeCommunication()
  const { showFooter } = useEmbedConfig()

  useEffect(() => {
    const { cleanup } = setupTranslationDetection(_translated => {
      setIsLanguageReady(true)
    }, 500)
    return cleanup
  }, [])

  useEffect(() => {
    const preloadCart = (state: any) => {
      state?.cart?.forEach((item: any) => preloadImages(resolveItemImageUrls(item)))
    }
    preloadCart(useCartStore.getState())
    const unsubCart = useCartStore.subscribe(preloadCart)

    const preloadCampaignAssets = (state: any) => {
      preloadImage(state?.campaign?.processing_gif?.file)
      preloadImage(state?.campaign?.logo?.file?.original || state?.campaign?.logo?.file)
    }
    preloadCampaignAssets(useSessionStore.getState())
    const unsubSession = useSessionStore.subscribe(preloadCampaignAssets)

    return () => {
      unsubCart()
      unsubSession()
    }
  }, [])

  // Gates the Footer and the `embedded` class; Header gates itself.
  const showFrame = !isEmbedded() || showFooter

  // No session yet: the retained campaign is last session's, so the chrome is a guess.
  // Logout screens are the exception — session-less by design.
  const isBootstrapping = !session && !location.pathname.includes('/logout')

  // Backend supplies arbitrary class tokens on `session.classes[]` for CSS
  // scoping; step-type adds `cp-main` / `order-tracking-container` / etc.
  const layoutClasses = useMemo(() => {
    const stepTypeNum = Number(session?.step_type)
    const isCustomerPortal =
      !!session &&
      (!session.is_popup || session.is_background_session) &&
      CUSTOMER_PORTAL_STEP_TYPES.includes(stepTypeNum)
    const isOrderTracking = stepTypeNum === InputType.ShipmentTracking
    return cn(
      'min-h-screen flex flex-col',
      getClassFromObject(session),
      isCustomerPortal && 'cp-main',
      isOrderTracking && 'order-tracking-container',
      !showFrame && 'embedded',
      session?.is_popup && 'is-popup'
    )
  }, [session, showFrame])

  // Checkout keeps the order summary flush under the header on mobile/tablet.
  const isCheckoutStep = Number(session?.step_type) === InputType.Checkout

  return (
    <div className={layoutClasses} style={{ backgroundColor: 'var(--content-background-color)' }}>
      <ToastContainer />
      {!isBootstrapping && <Header />}
      {showBanner && (
        <div
          className={`banner${session?.is_legacy ? '' : ' app-banner'}`}
          dangerouslySetInnerHTML={{ __html: bannerHtml }}
        />
      )}
      <Alert />

      <main role="main" className="relative flex-1 flex flex-col items-center w-full min-h-full">
        <GlobalLoading />
        <div className={cn('w-full flex-1 flex flex-col px-4 md:px-[5%] lg:pt-[1%]', isCheckoutStep ? 'pt-0' : 'pt-6')}>
          <RouteErrorBoundary>
            <Suspense fallback={<SuspenseFallback />}>
              <div key={location.pathname.split('/')[1] || 'root'} className="flex-1 flex flex-col animate-page-in">
                <Outlet />
              </div>
            </Suspense>
          </RouteErrorBoundary>
        </div>
      </main>

      {showFrame && isLanguageReady && !isBootstrapping && <Footer />}
      <Lifeline />
      <FloatingButtons />
    </div>
  )
}

export default Layout
